import { useState } from 'react'
import { copyToClipboard } from '~/utils'
import cx from 'classnames'
import { CheckCircle, Clipboard, ClipboardCheck, XCircle } from 'lucide-react'

type HandleMessageProps = {
  workspaceHandle?: string
  errorMessage: string | null
  successMessage: string | null
}

export const makeHandleUrl = (handle: string) => {
  return {
    forHumans: `${window.location.host}/${handle}`,
    forComputers: `${window.location.origin}/${handle}`,
  }
}

export const WorkspaceHandleMessage = ({ workspaceHandle, errorMessage, successMessage }: HandleMessageProps) => {
  const [handleCopied, setHandleCopied] = useState(false)

  const { forHumans, forComputers } = makeHandleUrl(workspaceHandle ?? '')

  const copyHandleToClipboard = () => {
    void copyToClipboard(forComputers, 'Shareable Workspace url').then(() => {
      setHandleCopied(true)
      setTimeout(() => setHandleCopied(false), 4000)
    })
  }

  const showError = !!errorMessage
  const showSuccess = !errorMessage && !!successMessage
  const showNeutral = !showError && !showSuccess
  const showNotset = showNeutral && !workspaceHandle
  const showCopy = showNeutral && workspaceHandle

  // this is to ignore the lint complain about complexity of the function
  const renderMessage = () => {
    if (showError) {
      return (
        <>
          <XCircle size={16} />
          {errorMessage}
        </>
      )
    } else if (showSuccess) {
      return (
        <>
          <CheckCircle size={16} />
          {successMessage}
        </>
      )
    } else if (showNotset) {
      return (
        <p>
          A Workspace Handle will allow to navigate directly to this Workspace using a pretty URL like so:{' '}
          <strong className="font-normal underline">{makeHandleUrl('super-team').forHumans}</strong>
        </p>
      )
    } else if (showCopy) {
      return (
        <span onClick={copyHandleToClipboard} className="cursor-pointer">
          This Workspace can be accessed by navigating to&nbsp;
          <strong className="meticulous-ignore mr-2 font-normal underline">{forHumans}</strong>
          {handleCopied && <ClipboardCheck size={16} className="inline-block" />}
          {!handleCopied && <Clipboard size={16} className="inline-block" />}
        </span>
      )
    } else {
      return null
    }
  }

  return (
    <p
      className={cx('text-xs font-light', {
        'text-neutral-900': showNeutral,
        'text-red-600': showError,
        'text-green-600': showSuccess,
        'flex items-center gap-1': showError || showSuccess,
      })}
    >
      {renderMessage()}
    </p>
  )
}
